import { useState } from "react";
import { ShieldCheck, FileText, Percent, Users, ArrowRight, CalendarDays, Clock } from "lucide-react";
import { COURSE_LIST } from "@/data/courses";
import BookingDialog from "@/components/site/BookingDialog";
import LpShell, { useLpMeta, GoogleStarsBadge } from "@/pages/lp/LpShell";
import LpLeadForm from "@/pages/lp/LpLeadForm";
import TrackedPhoneLink from "@/components/site/TrackedPhoneLink";
import AtlasPromo from "@/components/site/AtlasPromo";

const FEATURED_IDS = [
  "paket-gross",
  "paket-klein",
  "betreuungskraft",
  "praxisanleitung",
  "wundexperte",
  "palliativ-care",
];

const USPS = [
  { icon: FileText, text: "Buchung auf Rechnung – kein Vorkasse-Stress" },
  { icon: Percent, text: "Teamrabatt: bis −20 % ab 8 Teilnehmern" },
  { icon: ShieldCheck, text: "MDK-konforme Nachweise & Zertifikate" },
  { icon: Users, text: "Ganzes Team auf einmal anmelden – 2 Minuten" },
];

const TIERS = [
  ["ab 3 Teilnehmern", "−10 %"],
  ["ab 5 Teilnehmern", "−15 %"],
  ["ab 8 Teilnehmern", "−20 %"],
];

export default function LpPflegedienste() {
  useLpMeta(
    "Fortbildung für Pflegedienste · Team-Anmeldung & Rechnung · Dickies-Campus",
    "Pflichtfortbildungen & Weiterbildungen für ambulante Pflegedienste. Team-Anmeldung mit bis zu 20 % Rabatt, Buchung auf Rechnung, MDK-konforme Nachweise."
  );
  const [bookingCourse, setBookingCourse] = useState(null);
  const courses = FEATURED_IDS.map((id) => COURSE_LIST.find((c) => c.id === id)).filter(Boolean);

  return (
    <LpShell ctaLabel="Team anmelden" ctaHref="#lp-kurse">
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 pb-12 lg:pt-20">
        <div className="max-w-3xl">
          <GoogleStarsBadge />
          <h1 data-testid="lp-b2b-headline" className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-normal tracking-tight text-sand-800 leading-[1.05]">
            Fortbildung fürs ganze Team.<br />
            <span className="text-terra">Auf Rechnung. Mit Teamrabatt.</span>
          </h1>
          <p className="mt-5 text-sand-600 text-base md:text-lg leading-relaxed max-w-xl">
            Pflichtfortbildungen, Betreuungskräfte, Praxisanleiter & mehr – melden Sie
            mehrere Mitarbeiter:innen in einem Schritt an. 100 % online live, MDK-sicher dokumentiert.
          </p>
          <ul className="mt-7 grid sm:grid-cols-2 gap-3 max-w-xl">
            {USPS.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-2.5 text-[14px] text-sand-700">
                <Icon className="h-5 w-5 text-terra shrink-0" strokeWidth={1.7} />
                {text}
              </li>
            ))}
          </ul>
          <div className="mt-9 flex flex-wrap gap-4">
            <a href="#lp-kurse" data-testid="lp-b2b-cta-courses"
              className="inline-flex items-center gap-2 rounded-full bg-terra hover:bg-terra-dark text-white px-7 h-13 py-3.5 text-base font-semibold transition-colors group">
              Team jetzt anmelden
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.8} />
            </a>
            <a href="#lp-kontakt" data-testid="lp-b2b-cta-beratung"
              className="inline-flex items-center rounded-full border border-sand-400 hover:border-sand-800 text-sand-800 px-7 h-13 py-3.5 text-base font-medium transition-colors">
              Jahres-Fortbildungsplan anfragen
            </a>
          </div>
        </div>
      </section>

      {/* Rabattstaffel */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-6">
        <div data-testid="lp-b2b-discount-tiers" className="rounded-3xl bg-sand-800 text-cream-50 p-8 sm:p-10">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-7">
            <h2 className="font-display text-2xl sm:text-3xl">Ihr Teamrabatt – automatisch im Buchungsformular</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {TIERS.map(([label, pct]) => (
              <div key={label} className="rounded-2xl bg-white/8 border border-white/15 p-6 text-center">
                <div className="font-display text-4xl font-semibold text-peach-200">{pct}</div>
                <div className="text-[13.5px] text-cream-200 mt-1.5">{label}</div>
              </div>
            ))}
          </div>
          <p className="text-[13px] text-cream-200/80 mt-5">
            Gilt pro Kursbuchung · wird bei der Team-Anmeldung automatisch abgezogen – keine Gutscheincodes nötig.
          </p>
        </div>
      </section>

      {/* Kurse */}
      <section id="lp-kurse" className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <h2 className="font-display text-3xl text-sand-800 mb-3">Beliebt bei Pflegediensten</h2>
        <p className="text-sand-600 text-[14.5px] mb-8 max-w-2xl">
          Inklusive Pflichtfortbildungs-Pakete mit allen MDK-relevanten Themen (Hygiene, Notfall, Brandschutz, Expertenstandards u. v. m.).
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {courses.map((c) => (
            <div key={c.id} data-testid={`lp-course-${c.id}`}
              className="bg-white border border-cream-300 rounded-3xl p-7 flex flex-col shadow-soft hover:shadow-hover hover:-translate-y-1 transition-all">
              <h3 className="font-display text-[1.3rem] text-sand-800 leading-snug">{c.title}</h3>
              <p className="text-[13px] text-sand-600 mt-1.5">{c.subtitle}</p>
              <div className="mt-4 space-y-1.5 text-[13px] text-sand-700 flex-1">
                <div className="flex items-center gap-2"><Clock className="h-3.5 w-3.5 text-terra" strokeWidth={1.7} /> {c.weeks ? `${c.weeks} · ` : ""}{c.duration}</div>
                <div className="flex items-center gap-2"><CalendarDays className="h-3.5 w-3.5 text-terra" strokeWidth={1.7} /> Start: {c.nextStart}</div>
              </div>
              <div className="mt-5 pt-4 border-t border-cream-200 flex items-center justify-between">
                <div>
                  <div className="font-display text-xl font-semibold text-sand-800">{c.price}</div>
                  <div className="text-[11px] text-sand-500">{c.id.startsWith("paket-") ? "Festpreis · alle Mitarbeitenden" : "pro Teilnehmer:in"}</div>
                </div>
                <button type="button" data-testid={`lp-book-${c.id}`} onClick={() => setBookingCourse(c)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-terra hover:bg-terra-dark text-white px-5 py-2.5 text-[13px] font-semibold transition-colors">
                  {c.id.startsWith("paket-") ? "Jahresplan buchen" : "Team anmelden"} <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Atlas-Bridge · Beweis-Asset für PDL/QM: 253 S. gratis */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-6 lg:py-10">
        <AtlasPromo variant="ads" testId="lp-b2b-atlas-promo" />
      </section>

      {/* Kontakt */}
      <section id="lp-kontakt" className="bg-cream-100 border-t border-cream-300 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl text-sand-800 leading-tight">
              Jahres-Fortbildungsplan<br /><span className="text-terra">für Ihr Team?</span>
            </h2>
            <p className="mt-4 text-sand-600 text-[15px] leading-relaxed max-w-md">
              Wir stellen Ihnen einen MDK-sicheren Fortbildungsplan für Ihre Einrichtung zusammen –
              mit festen Terminen, Nachweisen und Paketpreis. Unverbindlich und kostenlos.
            </p>
            <TrackedPhoneLink source="lp-pflegedienste" className="mt-6 inline-flex items-center gap-2 text-lg font-semibold text-sand-800 hover:text-terra">
              📞 0211 29273330
            </TrackedPhoneLink>
          </div>
          <LpLeadForm source="ads-lp-pflegedienste" audience="pflegedienst"
            heading="Beratung für Einrichtungen"
            sub="Kurz Name und Nummer dalassen – wir melden uns mit einem Vorschlag für Ihr Team." />
        </div>
      </section>

      <BookingDialog course={bookingCourse} open={!!bookingCourse} onOpenChange={(v) => !v && setBookingCourse(null)}
        initialBuyerType="pflegedienst" />
    </LpShell>
  );
}
